// src/contexts/auth/dev-panel.tsx
/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useAuth } from './auth-context';

const panelCss = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999999;
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  background: rgba(10, 10, 12, 0.92);
  color: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
`;

const openPillCss = css`
  position: fixed;
  top: 8px;
  left: 50%;
  z-index: 999999;
`;

const innerCss = css`
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 10px 12px;
  max-height: 46vh;
  overflow: auto;
`;

const colCss = css`
  min-width: 260px;
  max-width: 520px;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const wideColCss = css`
  min-width: 360px;
  max-width: 680px;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const headerCss = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
`;

const titleCss = css`
  font-size: 12px;
  letter-spacing: 0.02em;
  opacity: 0.9;
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const badgeCss = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  opacity: 0.9;
`;

const btnRowCss = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
`;

const btnCss = (tone: 'neutral' | 'danger' | 'ok' = 'neutral') => css`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: ${tone === 'danger'
    ? 'rgba(255, 64, 64, 0.14)'
    : tone === 'ok'
      ? 'rgba(64, 255, 160, 0.12)'
      : 'rgba(255, 255, 255, 0.06)'};
  color: rgba(255, 255, 255, 0.92);
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 12px;
  cursor: pointer;
  line-height: 1;
  transition:
    transform 0.06s ease,
    background 0.15s ease,
    border-color 0.15s ease;
  &:hover {
    border-color: rgba(255, 255, 255, 0.22);
    background: ${tone === 'danger'
      ? 'rgba(255, 64, 64, 0.18)'
      : tone === 'ok'
        ? 'rgba(64, 255, 160, 0.16)'
        : 'rgba(255, 255, 255, 0.09)'};
  }
  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const inputCss = css`
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.92);
  border-radius: 10px;
  padding: 10px 10px;
  font-size: 12px;
  outline: none;

  &:focus {
    border-color: rgba(255, 255, 255, 0.28);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const smallLabelCss = css`
  font-size: 11px;
  opacity: 0.75;
`;

const boxCss = css`
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  padding: 10px;
`;

const preCss = css`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 11px;
  line-height: 1.35;
  opacity: 0.9;
`;

const hrCss = css`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  margin: 8px 0;
`;

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

export const AuthDevPanel: React.FC = () => {
  const auth = useAuth();

  // 1) show/hide entire panel (minimize to a small pill)
  const [showPanel, setShowPanel] = useState(false);

  // 2) collapse/expand body (keep header)
  const [collapsed, setCollapsed] = useState(false);

  const [emailDraft, setEmailDraft] = useState(auth.email ?? '');
  const [passwordDraft, setPasswordDraft] = useState(auth.password ?? '');
  const [stepDraft, setStepDraft] = useState(String(auth.currentStep ?? 0));

  const [patchText, setPatchText] = useState<string>(() => {
    try {
      return JSON.stringify(auth.onboarding ?? {}, null, 2);
    } catch {
      return '{}';
    }
  });

  const tokens = auth.dev?.tokens;
  const isBusy = !!auth.authLoading || !!auth.sessionLoading;

  const quickSnapshot = useMemo(() => {
    const onboardingKeys = Object.keys(auth.onboarding ?? {});
    return {
      hasSession: !!auth.session,
      hasUser: !!auth.user,
      userId: auth.userId,
      currentStep: auth.currentStep,
      onboardingKeys,
      error: auth.error,
      sessionLoading: auth.sessionLoading,
      authLoading: auth.authLoading,
      profileLoading: auth.profileLoading,
      loading: auth.loading,
      tokens,
    };
  }, [
    auth.authLoading,
    auth.currentStep,
    auth.error,
    auth.loading,
    auth.onboarding,
    auth.profileLoading,
    auth.session,
    auth.sessionLoading,
    auth.user,
    auth.userId,
    tokens,
  ]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // eslint-disable-next-line no-console
      console.log('[Auth][devpanel] copied to clipboard');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Auth][devpanel] clipboard failed', e);
    }
  };

  const doDump = () => {
    const d = auth.dev?.dump?.();
    try {
      // eslint-disable-next-line no-console
      console.log('[Auth][devpanel] dump()', d);
      void copyToClipboard(JSON.stringify(d ?? {}, null, 2));
    } catch {
      // ignore
    }
  };

  const applyOnboardingPatch = () => {
    try {
      const parsed = JSON.parse(patchText || '{}') as Record<string, any>;
      auth.setRegistration(parsed as any);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Auth][devpanel] invalid JSON patch', e);
    }
  };

  const setStep = () => {
    const n = Number(stepDraft);
    if (Number.isFinite(n)) auth.setCurrentStep(clamp(n, 0, 999));
  };

  const rehydratePatchText = () => {
    try {
      setPatchText(JSON.stringify(auth.onboarding ?? {}, null, 2));
    } catch {
      setPatchText('{}');
    }
  };

  // ✅ minimized state: only show an "Open" pill
  if (!showPanel) {
    return (
      <div css={openPillCss}>
        <button css={btnCss('neutral')} onClick={() => setShowPanel(true)}>
          Open Dev Panel
        </button>
      </div>
    );
  }

  return (
    <div css={panelCss} role="region" aria-label="Auth Dev Panel">
      <div css={headerCss}>
        <div css={titleCss}>
          <button css={btnCss('danger')} onClick={() => setShowPanel(false)}>
            Hide
          </button>
          <span>
            Auth Dev Panel <span css={badgeCss}>• ENVIRONMENT=DEV</span>
          </span>
        </div>

        <div css={btnRowCss}>
          <button css={btnCss('neutral')} onClick={() => setCollapsed((v) => !v)}>
            {collapsed ? 'Expand' : 'Collapse'}
          </button>
          <button css={btnCss('neutral')} onClick={() => auth.syncSession()} disabled={isBusy}>
            syncSession()
          </button>
          <button css={btnCss('neutral')} onClick={doDump}>
            dump() + copy
          </button>
          <button css={btnCss('danger')} onClick={() => auth.dev?.clearState?.()} disabled={isBusy}>
            clearState()
          </button>
          <button css={btnCss('danger')} onClick={() => auth.dev?.hardLogout?.()} disabled={isBusy}>
            hardLogout()
          </button>
        </div>
      </div>

      {collapsed ? null : (
        <div css={innerCss}>
          {/* Column 1: Status */}
          <div css={colCss}>
            <div css={boxCss}>
              <div css={smallLabelCss}>Quick snapshot</div>
              <hr css={hrCss} />
              <pre css={preCss}>{JSON.stringify(quickSnapshot, null, 2)}</pre>
            </div>

            <div css={boxCss}>
              <div css={smallLabelCss}>Token tools</div>
              <hr css={hrCss} />
              <div css={btnRowCss}>
                <button
                  css={btnCss('neutral')}
                  onClick={() => void copyToClipboard(JSON.stringify(tokens ?? {}, null, 2))}
                >
                  copy tokens snapshot
                </button>
                <button
                  css={btnCss('neutral')}
                  onClick={() => {
                    try {
                      const d = auth.dev?.dump?.();
                      void copyToClipboard(JSON.stringify(d ?? {}, null, 2));
                    } catch {
                      // ignore
                    }
                  }}
                >
                  copy full dump
                </button>
                <button
                  css={btnCss('neutral')}
                  onClick={() => window.dispatchEvent(new Event('auth:change'))}
                >
                  dispatch auth:change
                </button>
              </div>
            </div>
          </div>

          {/* Column 2: Login playground */}
          <div css={colCss}>
            <div css={boxCss}>
              <div css={smallLabelCss}>Login playground</div>
              <hr css={hrCss} />

              <div css={smallLabelCss}>email</div>
              <input
                css={inputCss}
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                placeholder="email"
                autoComplete="off"
              />

              <div css={smallLabelCss}>password</div>
              <input
                css={inputCss}
                value={passwordDraft}
                onChange={(e) => setPasswordDraft(e.target.value)}
                placeholder="password"
                autoComplete="off"
                type="password"
              />

              <div css={btnRowCss}>
                <button
                  css={btnCss('neutral')}
                  onClick={() => {
                    auth.setEmail(emailDraft);
                    auth.setPassword(passwordDraft);
                  }}
                >
                  setEmail/setPassword
                </button>

                <button css={btnCss('neutral')} onClick={() => auth.clearPassword()}>
                  clearPassword()
                </button>

                <button css={btnCss('neutral')} onClick={() => auth.resetLoginForm()}>
                  resetLoginForm()
                </button>
              </div>

              <hr css={hrCss} />

              <div css={btnRowCss}>
                <button
                  css={btnCss('ok')}
                  onClick={() => auth.signinWithPassword()}
                  disabled={isBusy}
                >
                  signinWithPassword()
                </button>
                <button css={btnCss('neutral')} onClick={() => auth.signOut()} disabled={isBusy}>
                  signOut()
                </button>
              </div>

              <hr css={hrCss} />

              <div css={btnRowCss}>
                <button
                  css={btnCss('neutral')}
                  onClick={() => auth.signInWithGoogle()}
                  disabled={isBusy}
                >
                  signInWithGoogle()
                </button>
                <button
                  css={btnCss('neutral')}
                  onClick={() => auth.signUpWithGoogle()}
                  disabled={isBusy}
                >
                  signUpWithGoogle()
                </button>
                <button
                  css={btnCss('neutral')}
                  // onClick={() => auth.biometricLogin()}
                  disabled={isBusy}
                >
                  biometricLogin()
                </button>
              </div>
            </div>

            <div css={boxCss}>
              <div css={smallLabelCss}>Step control</div>
              <hr css={hrCss} />
              <div css={smallLabelCss}>currentStep</div>
              <input
                css={inputCss}
                value={stepDraft}
                onChange={(e) => setStepDraft(e.target.value)}
                placeholder="step number"
                inputMode="numeric"
              />
              <div css={btnRowCss}>
                <button css={btnCss('neutral')} onClick={setStep} disabled={isBusy}>
                  setCurrentStep(step)
                </button>
                <button
                  css={btnCss('neutral')}
                  onClick={() => setStepDraft(String(auth.currentStep ?? 0))}
                >
                  load current
                </button>
              </div>
            </div>
          </div>

          {/* Column 3: Onboarding patcher */}
          <div css={wideColCss}>
            <div css={boxCss}>
              <div css={smallLabelCss}>Onboarding patcher (JSON)</div>
              <hr css={hrCss} />

              <textarea
                css={css`
                  ${inputCss};
                  min-height: 220px;
                  resize: vertical;
                `}
                value={patchText}
                onChange={(e) => setPatchText(e.target.value)}
                spellCheck={false}
              />

              <div css={btnRowCss}>
                <button css={btnCss('ok')} onClick={applyOnboardingPatch} disabled={isBusy}>
                  setRegistration(JSON)
                </button>
                <button css={btnCss('neutral')} onClick={rehydratePatchText}>
                  refresh from state
                </button>
                <button css={btnCss('neutral')} onClick={() => void copyToClipboard(patchText)}>
                  copy JSON
                </button>
              </div>

              <hr css={hrCss} />

              <div css={smallLabelCss}>
                Tip: You can include <b>registration_step</b> in JSON to drive the step, or use the
                Step control.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
